import deepMerge from "@/application/utils/deepMerge";
import type { VuetifyOptions } from "vuetify";
import type { AppData } from "@/application/types";
import { AppIdentifier } from "@/application/enums";

const apps = import.meta.glob<{ default: AppData }>("@/application/apps/*.ts", {
  eager: true,
});

const appData = Object.entries(apps).reduce((acc, [key, val]) => {
  const appIdentifier = key.match(/apps\/(.+).ts/)?.[1] as AppIdentifier;

  if (!appIdentifier) {
    throw new Error(`App not found: ${key}`);
  }

  return Object.assign(acc, {
    [appIdentifier]: val.default,
  });
}, {}) as Record<AppIdentifier, AppData>;

export const useApp = (app?: AppIdentifier) => {
  const appIdentifier =
    app ?? (import.meta.env.VITE_APP_IDENTIFIER as AppIdentifier);

  const currentAppData = appData[appIdentifier];

  const getAppConfig = (baseConfig: VuetifyOptions): VuetifyOptions => {
    if (!appIdentifier) {
      throw new Error("App identifier not provided");
    }

    if (!Object.keys(appData).includes(appIdentifier)) {
      throw new Error("App not found");
    }

    return deepMerge(baseConfig, appData[appIdentifier].vuetifyOptions);
  };

  return {
    appIdentifier,
    appData: currentAppData,
    getAppConfig,
  };
};
